import { useSearchParams } from 'react-router-dom'
import { compact } from 'lodash'
import { useMemo } from 'react'
import { useLocationIntegrationStatus } from 'app/hooks/useLocationIntegrationStatus'
import type { LocationProductType } from 'app/types/entities'
import { OptionType } from 'types/OptionType'

export const ALL_LOCATIONS_OPTION: OptionType = { name: `All Locations`, id: `0`, value: `` }

export type UseLocationFilterResult = {
  /** `locationIds` URL param, split + compacted. */
  locationIds: string[]
  locationOptions: OptionType[]
  isLoading: boolean
  /** Real locations currently selected (for the combobox). */
  selectedOptions: OptionType[]
  /** "Deselect all" option — just "All Locations". */
  deselectAllOptions: OptionType[]
  onLocationChange: (options: OptionType[]) => void
}

/**
 * Location filter wiring for the pipelines board: reads/writes the `locationIds` URL param
 * and exposes the option list + change handler for a multi-select `LocationSelector`.
 *
 * Intentionally locations-only — it has no PMS-integration pseudo-options ("All PMS
 * Integrated" / "All Non-PMS Integrated"). The marketing dashboard's own location control
 * keeps those (resolved server-side against its metrics API); this filters contacts by
 * `locationId`, where expanding an integration category to ids doesn't map cleanly, so the
 * two are deliberately separate rather than one shared implementation.
 */
export function useLocationFilter(locationProduct?: LocationProductType): UseLocationFilterResult {
  const [searchParams, setSearchParams] = useSearchParams()
  const locationIdsParam = searchParams.get(`locationIds`)
  const locationIds = useMemo(() => compact(locationIdsParam?.split(`,`) ?? []), [locationIdsParam])
  const { locationOptions, isLoading } = useLocationIntegrationStatus([locationIds], locationProduct)

  const selectedOptions = useMemo(
    () => locationOptions.filter((option) => !!option.value && locationIds.includes(option.value)),
    [locationOptions, locationIds],
  )

  const onLocationChange = (options: OptionType[]) => {
    setSearchParams((prev) => {
      const ids = compact(options.map((option) => option.value))
      if (ids.length) {
        prev.set(`locationIds`, ids.join(`,`))
      } else {
        prev.delete(`locationIds`)
      }
      return prev
    })
  }

  return {
    locationIds,
    locationOptions,
    isLoading,
    selectedOptions,
    deselectAllOptions: [ALL_LOCATIONS_OPTION],
    onLocationChange,
  }
}
